import {
  LuLayoutDashboard, LuStore, LuUsers, LuPackage, LuWarehouse, LuGift,
  LuShoppingCart, LuChartColumn, LuCreditCard, LuReceipt, LuMessageCircle, LuScanLine,
} from 'react-icons/lu';

// ── Design tokens partagés ──────────────────────────────────────
// Les mêmes valeurs existent en variables CSS dans index.css.
export const C = {
  bg:           '#F1F5F9',
  surface:      '#FFFFFF',
  sidebar:      '#0F172A',
  sidebarHover: '#1E293B',
  text:         '#0F172A',
  textSub:      '#475569',
  textMuted:    '#64748B',
  border:       '#E2E8F0',
  separator:    '#F1F5F9',
  success:      '#059669',
  successLight: '#D1FAE5',
  info:         '#2563EB',
  infoLight:    '#DBEAFE',
  warn:         '#D97706',
  warnLight:    '#FEF3C7',
  danger:       '#DC2626',
  dangerLight:  '#FEE2E2',
  purple:       '#7C3AED',
  purpleLight:  '#EDE9FE',
};

export const card = {
  backgroundColor: C.surface,
  borderRadius: 14,
  padding: '18px 20px',
  border: `1px solid ${C.border}`,
  boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
};

export const sectionTitle = {
  margin: '0 0 14px',
  fontWeight: 700,
  fontSize: 15,
  color: C.text,
};

// ── Profils : couleur d'accent, couleur du menu latéral, entrées du menu ─
export const ROLES = {
  admin: {
    label: 'Administrateur',
    accent: '#059669',
    accentLight: '#D1FAE5',
    sidebar: '#064E3B',
    nav: [
      { icon: LuLayoutDashboard, label: 'Dashboard',      path: '/admin' },
      { icon: LuStore,           label: 'Établissements', path: '/admin/etablissements' },
      { icon: LuUsers,           label: 'Utilisateurs',   path: '/admin/utilisateurs' },
      { icon: LuPackage,         label: 'Produits',       path: '/admin/produits' },
      { icon: LuWarehouse,       label: 'Stocks',         path: '/admin/stocks' },
      { icon: LuGift,            label: 'Kits',           path: '/admin/kits' },
      { icon: LuShoppingCart,    label: 'Ventes',         path: '/admin/ventes' },
      { icon: LuChartColumn,     label: 'Rapports',       path: '/admin/rapports' },
      { icon: LuMessageCircle,   label: 'Commandes en ligne', path: '/admin/commandes' },
      { icon: LuCreditCard,      label: 'Abonnements',    path: '/admin/abonnements' },
    ],
  },
  proprietaire: {
    label: 'Propriétaire',
    accent: '#EA580C',
    accentLight: '#FFEDD5',
    sidebar: '#7C2D12',
    nav: [
      { icon: LuLayoutDashboard, label: 'Dashboard', path: '/proprietaire' },
      { icon: LuUsers,           label: 'Employés',  path: '/proprietaire/employes' },
      { icon: LuPackage,         label: 'Produits',  path: '/proprietaire/produits' },
      { icon: LuGift,            label: 'Kits',      path: '/proprietaire/kits' },
      { icon: LuWarehouse,       label: 'Stocks',    path: '/proprietaire/stocks' },
      { icon: LuShoppingCart,    label: 'Ventes',    path: '/proprietaire/ventes' },
      { icon: LuMessageCircle,   label: 'Commandes en ligne', path: '/proprietaire/commandes' },
      { icon: LuScanLine,        label: 'Appro. par facture', path: '/proprietaire/approvisionnement' },
      { icon: LuCreditCard,      label: 'Abonnement', path: '/proprietaire/abonnement' },
    ],
  },
  gerant: {
    label: 'Gérant',
    accent: '#2563EB',
    accentLight: '#DBEAFE',
    sidebar: '#1E3A8A',
    nav: [
      { icon: LuLayoutDashboard, label: 'Dashboard', id: 'dashboard' },
      { icon: LuPackage,         label: 'Produits',  id: 'produits' },
      { icon: LuGift,            label: 'Kits',      id: 'kits' },
      { icon: LuShoppingCart,    label: 'Ventes',    id: 'ventes' },
      { icon: LuMessageCircle,   label: 'Commandes en ligne', id: 'commandes' },
      { icon: LuScanLine,        label: 'Appro. par facture', id: 'appro' },
      { icon: LuUsers,           label: 'Équipe',    id: 'equipe' },
    ],
  },
  serveur: {
    label: 'Serveur',
    accent: '#7C3AED',
    accentLight: '#EDE9FE',
    sidebar: '#4C1D95',
    nav: [
      { icon: LuShoppingCart, label: 'Caisse',     id: 'caisse' },
      { icon: LuReceipt,      label: 'Mes ventes', id: 'ventes' },
    ],
  },
  caissier: {
    label: 'Caissier',
    accent: '#0891B2',
    accentLight: '#CFFAFE',
    sidebar: '#164E63',
    nav: [
      { icon: LuShoppingCart, label: 'Caisse',     id: 'caisse' },
      { icon: LuReceipt,      label: 'Mes ventes', id: 'ventes' },
    ],
  },
};

// ── Formatage ───────────────────────────────────────────────────
// Montants en francs CFA, à la française : 6 599 800 FCFA (espaces insécables).
const NBSP = '\u00a0';
const nombre = n => {
  const v = typeof n === 'string' ? parseFloat(n.replace(/\s/g, '')) : Number(n);
  return Number.isFinite(v) ? v : 0;
};
/** Nombre entier groupé par milliers : 6 599 800 */
export const fmt = n => Math.round(nombre(n)).toLocaleString('fr-FR').replace(/[\u202f\s]/g, NBSP);
/** Montant complet : 6 599 800 FCFA */
export const fmtF = n => `${fmt(n)}${NBSP}FCFA`;
/** Forme courte pour les axes de graphiques : 750 k, 1,2 M */
export const fmtK = n => {
  const v = nombre(n);
  const court = (x, u) => `${x.toLocaleString('fr-FR', { maximumFractionDigits: x < 10 ? 1 : 0 })}${NBSP}${u}`;
  if (Math.abs(v) >= 1e9) return court(v / 1e9, 'Md');
  if (Math.abs(v) >= 1e6) return court(v / 1e6, 'M');
  if (Math.abs(v) >= 1e3) return court(v / 1e3, 'k');
  return fmt(v);
};
/** Sépare « 6 599 800 FCFA » en nombre + devise pour un affichage soigné. */
export const decouperMontant = valeur => {
  if (typeof valeur !== 'string') return null;
  const m = valeur.match(/^(.*?\d)\u00a0FCFA(.*)$/);
  return m ? { nombre: m[1], suite: m[2] } : null;
};
